<template>
    <div class="field" v-bind:class="{ inputError: hasError && isDirty }">
        <div class="label">
            {{ label }}
        </div>
        <div class="input">
            <swatches @close="onSwatchesClose" v-bind:value="value" v-on:input="updateValue" colors="text-advanced" show-fallback :disabled="disabled"></swatches>
        </div>
        <span class="tooltiptext">{{ errorText }}</span>
    </div>
</template>

<style scoped>
div.field{
    display: inline-flex;
    align-items: center;
    width: fit-content;
    flex-direction: column-reverse;
    margin: 0px 10px;
    max-width: 75px;
    min-width: 75px;
    text-align: center;
}

div.field.inputError div.label {
    color: var(--v-error-base, red);
    animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
    transition-timing-function: ease-in;
    transform: translate3d(0, 0, 0);
}

div.field.inputError div.input > .vue-swatches >>> div.vue-swatches__trigger{
    border: 2px solid var(--v-error-base, red);
}


/* Tooltip text */
div.field .tooltiptext {
    visibility: hidden;
    position: absolute;
    max-width: 120px;
    min-width: 120px;
    background-color: #555;
    color: #fff;
    text-align: center;
    padding: 5px 0;
    border-radius: 5px;
    z-index: 1;
    opacity: 0;
    margin-top: 8px;
    transform: translate(0px, -30px);
}

div.field.inputError .tooltiptext{
    -webkit-transition: all .5s ease 0s;
    -moz-transition: all .5s ease 0s;
    -o-transition: all .5s ease 0s;
    transition: all .5s ease 0s;
}

/* Show the tooltip text when you mouse over the tooltip container */
div.field.inputError:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
    transform: translate(0px, 0px);
}

@keyframes shake {
  20%, 60% {
    transform: translate3d(-1px, 0, 0);
  }
  
  40%, 80% {
    transform: translate3d(2px, 0, 0);
  }
}
</style>


<script>
import Swatches from 'vue-swatches'
export default {
    components: {
        Swatches,
    },
    props: {
        disabled: Boolean,
        error: Boolean,
        errorCount: {
            type: [Number, String],
            default: 1
        },
        errorMessages: {
            type: [String, Array],
            default: () => []
        },
        messages: {
            type: [String, Array],
            default: () => []
        },
        readonly: Boolean,
        rules: {
            type: Array,
            default: () => []
            },
        success: Boolean,
        successMessages: {
            type: [String, Array],
            default: () => []
        },
        validateOnBlur: Boolean,
        //
        form: Object,
        label: String,
        value: String
    },
    data() {
        return {
            isDirty: false,
            formAssign: false,
            errorBucket: [],
            hasColor: false,
            hasFocused: false,
            hasInput: false,
            isFocused: false,
            isResetting: false,
            valid: false
        }
    },
    methods: {
        onSwatchesClose(){
            this.isDirty = true;
        },
        updateValue(newValue){
            this.isDirty = true;
            this.$emit('input', newValue);
        },
        // //VForm validatable
        /** @public */
        reset () {
            this.isResetting = true
        },
        /** @public */
        resetValidation () {
            this.isResetting = true
        },
        /** @public */
        validate (force = false, value = this.internalValue) {
            const errorBucket = []

            if (force) this.hasInput = this.hasFocused = true

            for (let index = 0; index < this.rules.length; index++) {
                const rule = this.rules[index]
                const valid = typeof rule === 'function' ? rule(value) : rule

                if (valid === false || typeof valid === 'string') {
                    errorBucket.push(valid)
                } else if (valid !== true) {
                    console.log(`Rules should return a string or boolean, received '${typeof valid}' instead`, this)
                }
            }

            this.errorBucket = errorBucket
            this.valid = errorBucket.length === 0
            return this.valid
        }
    },
    mounted() {
    },
    beforeMount () {
        this.validate()
    },
    created () {
        this.form && this.form.register(this);
    },

    beforeDestroy () {
        this.form && this.form.unregister(this);
    },
    computed: {
        errorText() {
            return this.errorBucket[0];
        },
        internalValue () {
            return this.value;
        },
        hasError () {
            return this.internalErrorMessages.length > 0 ||
                this.errorBucket.length > 0 ||
                this.error
        },
        externalError () {
            return this.internalErrorMessages.length > 0 || this.error
        },
        // TODO: Add logic that allows the user to enable based
        // upon a good validation
        hasSuccess () {
            return this.successMessages.length > 0 ||
                this.success
        },
        hasMessages () {
            return this.validations.length > 0
        },
        hasState () {
            return this.hasSuccess || (this.shouldValidate && this.hasError)
        },
        internalErrorMessages () {
            return this.errorMessages || ''
        },
        shouldValidate () {
            return this.externalError || (!this.isResetting && (
                this.validateOnBlur
                ? this.hasFocused && !this.isFocused
                : (this.hasInput || this.hasFocused)
            ))
        },
        validations () {
            return this.validationTarget.slice(0, this.errorCount)
        },
        validationState () {
            if (this.hasError && this.shouldValidate) return 'error'
            if (this.hasSuccess) return 'success'
            if (this.hasColor) return this.color
            return null
        },
        validationTarget () {
            const target = this.internalErrorMessages.length > 0
                ? this.errorMessages
                : this.successMessages.length > 0
                ? this.successMessages
                : this.messages

            // String
            if (!Array.isArray(target)) {
                return [target]
            // Array with items
            } else if (target.length > 0) {
                return target
            // Currently has validation
            } else if (this.shouldValidate) {
                return this.errorBucket
            } else {
                return []
            }
        }
    },
    watch: {
        value(){
            this.isDirty = true;
        },
        form: {
            handler () {
                if(!this.formAssign && !!this.form){
                    this.formAssign = true;
                    this.form.register(this);
                }
            },
        },
        rules: {
            handler (newVal, oldVal) {
                if (deepEqual(newVal, oldVal)) return
                this.validate()
            },
            deep: true
        },
        internalValue () {
            // If it's the first time we're setting input,
            // mark it with hasInput
            this.hasInput = true
            this.validateOnBlur || this.$nextTick(this.validate)
        },
        isFocused (val) {
            // Should not check validation
            // if disabled or readonly
            if (
                !val &&
                !this.disabled &&
                !this.readonly
            ) {
                this.hasFocused = true
                this.validateOnBlur && this.validate()
            }
        },
        isResetting () {
            setTimeout(() => {
                this.hasInput = false
                this.hasFocused = false
                this.isResetting = false
            }, 0)
        },
        hasError (val) {
            if (this.shouldValidate) {
                this.$emit('update:error', val)
            }
        },
        hasFocused (val){
            if(val)this.isDirty = true;
        },
    }
}

export function deepEqual (a, b) {
  if (a === b) return true

  if (a instanceof Date && b instanceof Date) {
    // If the values are Date, they were convert to timestamp with getTime and compare it
    if (a.getTime() !== b.getTime()) return false
  }

  if (a !== Object(a) || b !== Object(b)) {
    // If the values aren't objects, they were already checked for equality
    return false
  }

  const props = Object.keys(a)

  if (props.length !== Object.keys(b).length) {
    // Different number of props, don't bother to check
    return false
  }

  return props.every(p => deepEqual(a[p], b[p]))
}
</script>